import type { OverviewChartRow } from "@nexus/contracts";
import {
  FORMAT_TO_DISPLAY,
  sumOverviewTotals,
  type MetricCatalogEntry,
  type MetricFormat,
} from "../../lib/metricCatalog.js";
import type { ChartMetricSelection } from "../../stores/overviewFilters.store.js";
import { formatMetricValue } from "@/features/dashboard/lib/format";

/**
 * Ticks drawn on every per-KPI axis, as a fraction of that KPI's own domain. Every
 * domain maps onto the same plot height, so these double as the rows the chart's
 * horizontal guide lines are drawn on.
 */
export const AXIS_TICK_FRACTIONS = [0, 0.25, 0.5, 0.75, 1] as const;

/**
 * Headroom reserved below each KPI's lowest value, as a fraction of its own spread.
 * Anchoring an axis at the exact minimum would render the lowest topic's bar at zero
 * height — invisible, and below MIN_LABELLED_PERCENT so it would lose its value label
 * too. A tenth of the spread leaves that bar a ~9% stub while the leader still reaches
 * the top of the plot.
 */
const AXIS_MIN_PADDING_RATIO = 0.1;

/** Below this percent of its axis a bar is too short to carry a value label. */
const MIN_LABELLED_PERCENT = 3;

/** Suffix for the raw values the bars plot; `id` holds the height fraction. */
export const RAW_METRIC_SUFFIX = "__raw";

export function rawMetricKey(metricId: string): string {
  return `${metricId}${RAW_METRIC_SUFFIX}`;
}

/** A selected metric resolved against the catalog, ready to render. */
export interface ResolvedMetric {
  id: string;
  label: string;
  format: MetricFormat;
  decimals: number;
  colorVar: string;
  sort: "asc" | "desc";
  /** Which pinned axis pane this KPI reads against. */
  axis: "left" | "right";
}

/** One X-axis category with each metric's raw value keyed at `id__raw`. */
export interface ChartDatum {
  category: string;
  [metricId: string]: string | number | null;
}

/** Lowest and highest raw value a KPI reaches across the filtered topics. */
export interface MetricRange {
  min: number;
  max: number;
}

/**
 * Raw value range per KPI across the charted topics — the basis for that KPI's axis.
 * Every filtered topic is charted, so each axis spans the full spread of its KPI,
 * including whatever sits in the long tail the user has to scroll to reach.
 */
export function computeMetricRanges(
  data: ChartDatum[],
  metrics: ResolvedMetric[],
): Record<string, MetricRange | null> {
  const ranges: Record<string, MetricRange | null> = {};
  for (const m of metrics) {
    let range: MetricRange | null = null;
    for (const datum of data) {
      const v = datum[rawMetricKey(m.id)];
      if (typeof v !== "number") continue;
      range =
        range === null
          ? { min: v, max: v }
          : { min: Math.min(range.min, v), max: Math.max(range.max, v) };
    }
    ranges[m.id] = range;
  }
  return ranges;
}

/** Each KPI's padded axis domain, keyed by metric id. */
export function computeMetricDomains(
  data: ChartDatum[],
  metrics: ResolvedMetric[],
): Record<string, [number, number]> {
  const ranges = computeMetricRanges(data, metrics);
  return Object.fromEntries(metrics.map((m) => [m.id, metricAxisDomain(ranges[m.id] ?? null)]));
}

/**
 * The `[bottom, top]` domain for one KPI's axis: its own spread, padded below so the
 * lowest topic keeps a visible bar. A KPI with no spread — a single topic, or every
 * topic tied — falls back to a zero baseline so its bars render full height rather
 * than collapsing onto a degenerate domain.
 *
 * The padded bottom is clamped at zero for non-negative KPIs. Recharts anchors bars to
 * zero whenever a domain straddles it, so a sub-zero bottom would leave every bar
 * floating above the axis with the padding strip showing as a gap underneath.
 */
export function metricAxisDomain(range: MetricRange | null): [number, number] {
  if (range === null) return [0, 1];
  const spread = range.max - range.min;
  if (spread === 0) return range.max === 0 ? [0, 1] : [Math.min(0, range.max), range.max];
  const padded = range.min - spread * AXIS_MIN_PADDING_RATIO;
  return [range.min >= 0 ? Math.max(0, padded) : padded, range.max];
}

/** Evenly spaced ticks across a KPI's own domain. */
export function metricAxisTicks(domain: [number, number]): number[] {
  const [bottom, top] = domain;
  return AXIS_TICK_FRACTIONS.map((f) => bottom + (top - bottom) * f);
}

/** Where a raw value sits within its axis, as a percent — i.e. the bar's height. */
export function domainPercent(raw: number | null, domain: [number, number]): number | null {
  if (raw === null) return null;
  const [bottom, top] = domain;
  if (top === bottom) return null;
  return ((raw - bottom) / (top - bottom)) * 100;
}

/** Resolve store selections against the catalog, dropping unknown ids. */
export function resolveMetrics(
  chartMetrics: ChartMetricSelection[],
  catalogById: Record<string, MetricCatalogEntry>,
): ResolvedMetric[] {
  return chartMetrics.flatMap((m) => {
    const entry = catalogById[m.id];
    if (!entry) return [];
    return [{
      id: m.id,
      label: entry.label,
      format: entry.format,
      decimals: entry.decimals,
      colorVar: entry.colorVar,
      sort: m.sort,
      axis: m.axis,
    }];
  });
}

/** Comma-free title of the selected metrics, e.g. "Spend · CPA". */
export function metricsTitle(metrics: ResolvedMetric[]): string {
  return metrics.map((m) => m.label).join(" · ");
}

/**
 * Pivot per-topic rows into recharts data, ordered by the active sort metric's raw
 * value (defaults to `metrics[0]`). Every topic is kept — the chart scrolls sideways
 * rather than cutting the tail. Bars plot the raw values at `{id}__raw` against that
 * KPI's own axis; bar height is derived from the axis domain at render time, never
 * stored here.
 */
export function buildChartData(
  rows: OverviewChartRow[],
  metrics: ResolvedMetric[],
  catalogById: Record<string, MetricCatalogEntry>,
  sortMetricId?: string,
): ChartDatum[] {
  if (metrics.length === 0 || rows.length === 0) return [];

  const totals = sumOverviewTotals(rows);

  const data: ChartDatum[] = rows.map((row) => {
    const datum: ChartDatum = { category: row.Topic_Title };
    for (const m of metrics) {
      datum[rawMetricKey(m.id)] = catalogById[m.id]?.compute(row, totals) ?? null;
    }
    return datum;
  });

  const sortMetric = metrics.find((m) => m.id === sortMetricId) ?? metrics[0]!;
  const dir = sortMetric.sort === "asc" ? 1 : -1;
  data.sort((a, b) => {
    const av = a[rawMetricKey(sortMetric.id)];
    const bv = b[rawMetricKey(sortMetric.id)];
    const an = typeof av === "number";
    const bn = typeof bv === "number";
    if (!an && !bn) return 0;
    if (!an) return 1;
    if (!bn) return -1;
    return (av - bv) * dir;
  });

  return data;
}

/** Format the raw KPI value — tooltips, bar labels, axis ticks, and the table. */
export function formatRawMetric(
  metric: ResolvedMetric,
  v: number | null,
  currencySymbol = "$",
): string {
  if (v === null) return "—";
  return formatMetricValue(v, FORMAT_TO_DISPLAY[metric.format], currencySymbol, metric.decimals);
}

/**
 * Raw KPI text for a bar label. Visibility keys off the height fraction, since that
 * is what determines how much room the bar actually has.
 */
export function formatBarLabel(
  metric: ResolvedMetric,
  datum: ChartDatum,
  currencySymbol: string,
  domain: [number, number],
): string {
  const value = datum[rawMetricKey(metric.id)];
  const raw = typeof value === "number" ? value : null;
  if (!shouldShowBarLabel(domainPercent(raw, domain))) return "";
  return formatRawMetric(metric, raw, currencySymbol);
}

/**
 * Every bar carries its value; only a negligible slice is skipped, because
 * there is no room to place a label over a bar that is barely off the axis.
 */
function shouldShowBarLabel(value: number | null): boolean {
  if (value === null) return false;
  return value >= MIN_LABELLED_PERCENT;
}
